import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Avatar,
    Chip,
    Divider,
    Button,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Link,
} from '@mui/material';
import {
    Person,
    Email,
    Phone,
    CalendarToday,
    ArrowBack,
    AssignmentInd,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserById } from '../../services/Api';

const ViewUserDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchUserDetails = async () => {
            setLoading(true);
            try {
                const response = await getUserById(id);
                if (response.success) {
                    setUser(response.data);
                } else {
                    setError(response.message || 'Failed to fetch user details');
                }
            } catch (err) {
                setError(err.response?.data?.message || err.message || 'An error occurred while fetching user details');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchUserDetails();
    }, [id]);

    const InfoRow = ({ icon: Icon, label, value }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                backgroundColor: 'rgba(0,0,0,0.03)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                border: '1px solid',
                borderColor: 'divider'
            }}>
                <Icon sx={{ color: 'primary.main', fontSize: 20 }} />
            </Box>
            <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.2 }}>
                    {label}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {value === null || value === undefined || value === '' ? '-' : value}
                </Typography>
            </Box>
        </Box>
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                <Button startIcon={<ArrowBack />} onClick={() => navigate('/users')}>
                    Back to Users
                </Button>
            </Box>
        );
    }

    if (!user) return null;

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/users');
                        }}
                    >
                        Users
                    </Link>
                    <Typography color="text.primary">User Details</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 300, display: 'flex', alignItems: 'center' }}>
                        <AssignmentInd sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                        User Details
                    </Typography>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/users')}
                        variant="outlined"
                        sx={{ borderRadius: '8px' }}
                    >
                        Back to List
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={4}>
                {/* Profile Overview Card */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper elevation={0} sx={{
                        p: 4,
                        textAlign: 'center',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        height: '100%'
                    }}>
                        <Avatar
                            src={user.profileImage}
                            sx={{
                                width: 120,
                                height: 120,
                                margin: '0 auto 24px',
                                border: '4px solid',
                                borderColor: 'primary.light',
                                boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                            }}
                        >
                            {!user.profileImage && <Person sx={{ fontSize: 60 }} />}
                        </Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                            {user.name || '-'}
                        </Typography>
                        <Chip
                            label={user.deletedAt ? "Account deleted" : "Account Active"}
                            color={user.deletedAt ? "error" : "success"}
                            sx={{ fontWeight: 600, borderRadius: '8px', mb: 3 }}
                        />
                        <Divider sx={{ mb: 3 }} />
                        <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                                System Info
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">Created At</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {user.createdAt ? user.createdAt.substring(0, 10) : '-'}
                                </Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">Updated At</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {user.updatedAt ? user.updatedAt.substring(0, 10) : '-'}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Personal Information Card */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper elevation={0} sx={{
                        p: 4,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        height: '100%'
                    }}>
                        <Typography variant="h6" sx={{ mb: 4, fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                            Personal Information
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                                <InfoRow
                                    icon={Person}
                                    label="Username"
                                    value={user.name}
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <InfoRow
                                    icon={Email}
                                    label="Email Address"
                                    value={user.email}
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <InfoRow
                                    icon={Phone}
                                    label="Mobile Number"
                                    value={user.phoneNumber || user.mobile}
                                />
                            </Grid>
                            <Grid size={{ xs: 6 }}>
                                <InfoRow
                                    icon={CalendarToday}
                                    label="Agree Terms"
                                    value={user.isAgreeTerms ? "true" : "false"}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ViewUserDetails;
